import React from 'react';
import { Link } from 'react-router-dom';
import type { BlogPost } from '@/types/blog';

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
}

export function BlogCard({ post, featured = false }: BlogCardProps) {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (featured) {
    return (
      <Link
        to={`/blog/${post.slug}`}
        className="group block rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow"
      >
        {post.featured_image && (
          <div className="aspect-[2/1] overflow-hidden">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}
        <div className="p-6">
          {post.category_name && (
            <span className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">
              {post.category_name}
            </span>
          )}
          <h2 className="text-2xl font-bold text-gray-900 group-hover:text-emerald-600 transition-colors mb-2">
            {post.title}
          </h2>
          {post.excerpt && (
            <p className="text-gray-600 line-clamp-2 mb-3">{post.excerpt}</p>
          )}
          <div className="flex items-center gap-3 text-sm text-gray-500">
            {post.author_name && <span>{post.author_name}</span>}
            {post.author_name && post.published_at && <span>·</span>}
            {post.published_at && <span>{formatDate(post.published_at)}</span>}
            {post.reading_time && (
              <>
                <span>·</span>
                <span>{post.reading_time} min read</span>
              </>
            )}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow"
    >
      {post.featured_image && (
        <div className="aspect-[3/2] overflow-hidden">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}
      <div className="p-5">
        {post.category_name && (
          <span className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">
            {post.category_name}
          </span>
        )}
        <h3 className="text-lg font-bold text-gray-900 group-hover:text-emerald-600 transition-colors mb-1 line-clamp-2">
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{post.excerpt}</p>
        )}
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {post.published_at && <span>{formatDate(post.published_at)}</span>}
          {post.reading_time && (
            <>
              <span>·</span>
              <span>{post.reading_time} min read</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
